'use client';

import Image from 'next/image';

import {
  ArrowRight,
  Building2,
  CalendarCheck,
  CheckCircle,
  Dumbbell,
  HeartPulse,
  type LucideIcon,
  ShoppingBag,
  Stethoscope,
  UtensilsCrossed,
  Wrench,
} from 'lucide-react';

import { BentoGrid, type BentoCard } from '@/components/sections/bento-grid';
import { CustomerVideos } from '@/components/sections/customer-videos';
import HeroCentered from '@/components/sections/hero-centered';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { SubtleEntrance } from '@/components/ui/subtle-entrance';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

const DOCS_URL = 'https://docs.open.cx/partners/introduction';

/* Icons for the "verticals" grid, indexed to match CONTENT.*.verticals. */
const VERTICAL_ICONS: LucideIcon[] = [
  Stethoscope,
  UtensilsCrossed,
  Building2,
  ShoppingBag,
  Wrench,
  Dumbbell,
  HeartPulse,
  CalendarCheck,
];

/* -------------------------------------------------------------------------- */
/*  Bento grid — the white-label capabilities. Same treatment as the home     */
/*  page: one wide card on top, three vertical cards below.                    */
/* -------------------------------------------------------------------------- */

const BENTO_SECTION = {
  en: {
    title: 'Everything you need to ship it',
    subtitle:
      'You bring the customers and the brand. Open brings the AI, the infrastructure, and the scale, all behind the scenes.',
  },
  ar: {
    title: 'كل اللي يلزمك عشان تطلقه',
    subtitle:
      'العملاء والعلامة التجارية منك، والـ AI والبنية التحتية والقدرة على التوسّع من Open — كله من وراء الكواليس.',
  },
} as const;

const BENTO_CARDS_EN: BentoCard[] = [
  {
    id: 'brand',
    title: 'Your brand, top to bottom',
    description:
      'Your logo, your product name, your support email. Your customers get enterprise AI support and never see Open.',
    image: 'https://platform.open.cx/changelog/versioning-audit.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'White-label',
      headline: 'They see your brand, not ours',
      lead: 'From the dashboard your customers log into, to the emails they receive, everything carries your name.',
      blocks: [
        {
          type: 'text',
          body: 'Set your logo, product name, and support email once, and they flow across the entire experience. To your customers, this is your product, a natural extension of the software they already pay you for.',
        },
        {
          type: 'list',
          heading: 'Yours to control',
          items: [
            'Your logo and product name everywhere',
            'Your support email as the sender',
            'Your colors and domain',
            'Your pricing and your invoices',
          ],
        },
      ],
      cta: { label: 'Become a partner', href: '/demo' },
    },
  },
  {
    id: 'provision',
    title: 'Launch a customer with one API call',
    description:
      'Provision a fully configured AI support account, agent, widget, and knowledge base, with a single request.',
    image: 'https://platform.open.cx/changelog/companion-planning-mode.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Partner API',
      headline: 'One request, a live account',
      lead: 'When a customer signs up on your platform, you call one endpoint and they are ready to go.',
      blocks: [
        {
          type: 'text',
          body: 'No manual setup, no support tickets, no waiting. The account is created with your defaults already applied, and you get back a widget token to embed on your customer’s site. That is the whole integration.',
        },
        {
          type: 'stats',
          items: [
            { value: '1', label: 'API call to launch' },
            { value: '<5 min', label: 'To first live agent' },
            { value: '0', label: 'Manual setup' },
          ],
        },
      ],
      cta: { label: 'Read the docs', href: '/demo' },
    },
  },
  {
    id: 'control',
    title: 'Full control for every account',
    description:
      'Each customer gets their own AI agent, knowledge base, and API key, all manageable programmatically.',
    image: 'https://platform.open.cx/changelog/ai-instructions-ui.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Per-account power',
      headline: 'Every account is fully yours to run',
      lead: 'Train the AI, crawl a website, manage contacts, all through the API, at any scale.',
      blocks: [
        {
          type: 'text',
          body: 'Every customer org is isolated and comes with its own API key. You can automate onboarding end to end: point the AI at their help center, set their guardrails, and turn on the channels they need, without anyone logging in.',
        },
      ],
      cta: { label: 'Become a partner', href: '/demo' },
    },
  },
  {
    id: 'scale',
    title: 'Scale from 10 to 25,000',
    description:
      'Provision and run as many customer accounts as you can sell. The platform handles the load.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'Built to scale',
      headline: 'Grow without re-architecting',
      lead: 'Whether you onboard ten customers or twenty-five thousand, the same API and the same infrastructure carry you.',
      blocks: [
        {
          type: 'text',
          body: 'You never hit a ceiling and never manage servers. As your customer base grows, Open scales underneath you, so the only thing you focus on is selling and supporting your product line.',
        },
      ],
      cta: { label: 'Talk to us', href: '/demo' },
    },
  },
];

const BENTO_CARDS_AR: BentoCard[] = [
  {
    id: 'brand',
    title: 'علامتك التجارية، من أولها إلى آخرها',
    description:
      'شعارك، واسم منتجك، وإيميل الدعم الخاص بك. عملاؤك يحصلون على دعم ذكي بمستوى المؤسسات، وما يشوفون Open أبداً.',
    image: 'https://platform.open.cx/changelog/versioning-audit.jpg',
    layout: 'horizontal',
    modal: {
      eyebrow: 'بعلامتك التجارية',
      headline: 'يشوفون علامتك، لا علامتنا',
      lead: 'من لوحة التحكم اللي يدخلها عملاؤك، إلى آخر رسالة تصلهم — كل شيء يحمل اسمك.',
      blocks: [
        {
          type: 'text',
          body: 'حدّد شعارك واسم منتجك وإيميل الدعم مرة وحدة، وبعدها تظهر في كل نقطة يمرّ بها العميل. بالنسبة لعملائك، هذا منتجك أنت — امتداد طبيعي للبرنامج اللي يدفعون لك عليه أصلاً.',
        },
        {
          type: 'list',
          heading: 'كلها بيدك',
          items: [
            'شعارك واسم منتجك في كل مكان',
            'إيميل الدعم الخاص بك هو المُرسِل',
            'ألوانك ونطاقك',
            'تسعيرك وفواتيرك',
          ],
        },
      ],
      cta: { label: 'صير شريك معنا', href: '/demo' },
    },
  },
  {
    id: 'provision',
    title: 'أطلق عميلاً جديداً باستدعاء API واحد',
    description:
      'جهّز حساب دعم ذكي كامل — وكيل، وأداة دردشة، وقاعدة معرفة — بطلب واحد.',
    image: 'https://platform.open.cx/changelog/companion-planning-mode.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'واجهة الشركاء',
      headline: 'طلب واحد، وحساب شغّال',
      lead: 'لمّا يسجّل عميل عندك، تستدعي نقطة نهاية وحدة — ويكون جاهزاً فوراً.',
      blocks: [
        {
          type: 'text',
          body: 'بدون إعداد يدوي، وبدون تذاكر دعم، وبدون انتظار. يصلك الحساب وإعداداتك الافتراضية مطبّقة عليه، ومعه رمز أداة الدردشة تركّبه على موقع عميلك. هذا كل التكامل.',
        },
        {
          type: 'stats',
          items: [
            { value: '١', label: 'استدعاء API للإطلاق' },
            { value: '<٥ دقائق', label: 'لأول وكيل شغّال' },
            { value: '٠', label: 'إعداد يدوي' },
          ],
        },
      ],
      cta: { label: 'اقرأ التوثيق', href: '/demo' },
    },
  },
  {
    id: 'control',
    title: 'تحكّم كامل لكل حساب',
    description:
      'كل عميل يحصل على وكيله وقاعدة معرفته ومفتاح API خاص به — وكلها تديرها برمجياً.',
    image: 'https://platform.open.cx/changelog/ai-instructions-ui.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'لكل حساب على حده',
      headline: 'كل حساب تملكه وتشغّله بالكامل',
      lead: 'درّب الـ AI، واسحب محتوى موقع، وأدر جهات الاتصال — كلها عبر الـ API، وبأي حجم.',
      blocks: [
        {
          type: 'text',
          body: 'كل مؤسسة عميل معزولة، ومعها مفتاح API خاص بها. أتمت الإعداد من أوله إلى آخره: وجّه الـ AI لمركز المساعدة الخاص بهم، وحدّد ضوابطهم، وشغّل القنوات اللي يحتاجونها — بدون ما يسجّل أحد دخوله.',
        },
      ],
      cta: { label: 'صير شريك معنا', href: '/demo' },
    },
  },
  {
    id: 'scale',
    title: 'توسّع من ١٠ إلى ٢٥ ألف',
    description:
      'جهّز وشغّل أي عدد من الحسابات تقدر تبيعه. والمنصة تتحمّل الضغط.',
    image: 'https://platform.open.cx/changelog/phone-agent-workflows.jpg',
    layout: 'vertical',
    modal: {
      eyebrow: 'مبني للتوسّع',
      headline: 'اكبر بدون ما تعيد بناء أي شيء',
      lead: 'سواء ضممت عشرة عملاء أو خمسة وعشرين ألفاً — نفس الـ API ونفس البنية التحتية تحملك.',
      blocks: [
        {
          type: 'text',
          body: 'لا سقف تصل إليه، ولا خوادم تديرها. وكلما كبرت قاعدة عملائك، يتوسّع Open تحتك. ويبقى عملك الوحيد: إنك تبيع وتدعم خط منتجك.',
        },
      ],
      cta: { label: 'كلّمنا', href: '/demo' },
    },
  },
];

const CONTENT = {
  en: {
    heroTitleA: 'Add AI customer support',
    heroTitleB: 'to what you already sell',
    heroSubtitle:
      'Offer enterprise-grade AI support to your customers under your own brand. Open runs the technology behind the scenes. You launch a new product line, open a new revenue stream, and keep every customer relationship.',
    heroCta: 'Become a partner',

    spotlightEyebrow: 'Proven at scale',
    spotlightHeading:
      'FareHarbor, part of the Booking.com group, runs on Open.',
    spotlightCopy:
      'One of the world’s largest travel companies white-labels Open to give thousands of its merchants AI customer support, under its own brand. If it scales for them, it scales for you.',
    spotlightStat: '1,000s',
    spotlightStatLabel: 'of merchants served under a partner’s brand',

    verticalsTitle: 'Add it to the platform you already sell',
    verticalsSubtitle:
      'If you sell software to an industry, your customers already ask you for better support tools. Now you can sell them one, as your own new product line.',
    verticals: [
      {
        title: 'Dental & clinic CRMs',
        description:
          'Give your practices an AI receptionist that books and answers patients 24/7.',
      },
      {
        title: 'Restaurant & POS platforms',
        description:
          'Offer every restaurant instant support for orders and reservations.',
      },
      {
        title: 'Property management software',
        description:
          'Let landlords resolve tenant questions automatically, on your platform.',
      },
      {
        title: 'E-commerce platforms',
        description:
          'Add AI support every merchant can switch on with a single click.',
      },
      {
        title: 'Field service software',
        description:
          'Handle scheduling and status questions for the trades, around the clock.',
      },
      {
        title: 'Fitness & gym software',
        description:
          'Answer membership, booking, and class questions for every location.',
      },
      {
        title: 'Healthcare SaaS',
        description:
          'Triage patient questions in 100+ languages, on your rules.',
      },
      {
        title: 'Booking & marketplace platforms',
        description:
          'Give sellers instant, on-brand support without hiring a team.',
      },
    ],

    stepsTitle: 'Three steps to a new revenue line',
    steps: [
      {
        step: 'Connect the Partner API',
        copy: 'Plug one endpoint into your platform. Set your branding and defaults once.',
      },
      {
        step: 'Provision accounts under your brand',
        copy: 'Create a ready-to-go AI support account for each customer, automatically.',
      },
      {
        step: 'Bill your customers, keep the margin',
        copy: 'You own pricing and the relationship. Pay for usage, mark it up how you like.',
      },
    ],

    featuresTitle: 'What you get as a partner',
    featuresSubtitle:
      'The full platform, wrapped in your brand and priced for a business you own.',
    features: [
      {
        title: 'White-label dashboard',
        description:
          'Your logo, app name, and support email across the whole product.',
      },
      {
        title: 'Single provisioning API',
        description:
          'Create a ready-to-go account with one request. No manual setup.',
      },
      {
        title: 'Per-account API keys',
        description:
          'Train the AI, crawl sites, and manage contacts programmatically.',
      },
      {
        title: 'Usage-based pricing',
        description:
          'Pay for what your customers use, and set your own price on top.',
      },
      {
        title: 'Isolated and secure',
        description:
          'Every account is fully separated, with enterprise-grade security.',
      },
      {
        title: 'Embed in minutes',
        description:
          'Drop in a widget token and the AI is live on your customer’s site.',
      },
      {
        title: 'All channels included',
        description:
          'Chat, email, WhatsApp, and social for every account you create.',
      },
      {
        title: '100+ languages',
        description: 'Serve your customers’ customers anywhere in the world.',
      },
      {
        title: 'Dedicated partner team',
        description: 'Named people who help you launch, integrate, and grow.',
      },
      {
        title: 'You own the relationship',
        description: 'Billing, branding, and the account all stay with you.',
      },
    ],

    ctaHeading: 'Ready to launch your own AI support product?',
    ctaCopy:
      'Add a new product line without building the AI, the infrastructure, or the team. We will help you go from idea to live in weeks.',
    ctaPrimary: 'Become a partner',
    ctaSecondary: 'Read the partner docs',
  },

  ar: {
    heroTitleA: 'أضف دعم عملاء بالذكاء الاصطناعي',
    heroTitleB: 'للمنتج اللي تبيعه أصلاً',
    heroSubtitle:
      'قدّم لعملائك دعماً بمستوى المؤسسات بالذكاء الاصطناعي، تحت علامتك التجارية. Open يشغّل التقنية من وراء الكواليس، وأنت تطلق خط منتج جديد، وتفتح مصدر دخل جديد، وتحتفظ بعلاقتك مع كل عميل.',
    heroCta: 'صير شريك معنا',

    spotlightEyebrow: 'مُجرّب على نطاق واسع',
    spotlightHeading: 'FareHarbor، جزء من مجموعة Booking.com، يستخدم Open.',
    spotlightCopy:
      'واحدة من أكبر شركات السفر في العالم تشغّل Open تحت علامتها الخاصة، وتقدّم عبرها دعم عملاء بالـ AI لآلاف التجار. إذا توسّع معهم، يتوسّع معك.',
    spotlightStat: 'آلاف',
    spotlightStatLabel: 'من التجار يتلقّون الدعم تحت علامة شريك',

    verticalsTitle: 'أضفه للمنصة اللي تبيعها أصلاً',
    verticalsSubtitle:
      'تبيع برامج لقطاع معيّن؟ عملاؤك أصلاً يطلبون منك أدوات دعم أفضل. والآن تقدر تبيعهم واحدة — كخط منتج جديد باسمك.',
    verticals: [
      {
        title: 'أنظمة CRM لعيادات الأسنان',
        description:
          'موظّف استقبال ذكي لكل عيادة — يحجز ويردّ على المرضى ٢٤/٧.',
      },
      {
        title: 'منصات المطاعم ونقاط البيع',
        description: 'قدّم لكل مطعم دعماً فورياً للطلبات والحجوزات.',
      },
      {
        title: 'برامج إدارة العقارات',
        description:
          'مكّن الملّاك من حلّ أسئلة المستأجرين تلقائياً، على منصتك.',
      },
      {
        title: 'منصات التجارة الإلكترونية',
        description: 'أضف دعماً ذكياً يشغّله كل تاجر بضغطة وحدة.',
      },
      {
        title: 'برامج الخدمة الميدانية',
        description:
          'يتكفّل بأسئلة المواعيد وحالة الطلب للحرفيين، على مدار الساعة.',
      },
      {
        title: 'برامج النوادي واللياقة',
        description: 'أجب عن أسئلة العضويات والحجوزات والحصص في كل فرع.',
      },
      {
        title: 'برامج الرعاية الصحية',
        description: 'افرز أسئلة المرضى في +١٠٠ لغة، حسب قواعدك.',
      },
      {
        title: 'منصات الحجز والأسواق',
        description: 'دعم فوري بعلامتك للبائعين، بلا توظيف فريق.',
      },
    ],

    stepsTitle: 'ثلاث خطوات لمصدر دخل جديد',
    steps: [
      {
        step: 'اربط واجهة الشركاء',
        copy: 'وصّل نقطة نهاية وحدة بمنصتك، وحدّد علامتك وإعداداتك مرة وحدة بس.',
      },
      {
        step: 'جهّز الحسابات تحت علامتك',
        copy: 'كل عميل جديد يحصل على حساب دعم ذكي جاهز، تلقائياً.',
      },
      {
        step: 'أصدر فواتيرك واحتفظ بالهامش',
        copy: 'التسعير لك والعلاقة لك. تدفع مقابل الاستخدام، وتحدّد ربحك فوقه.',
      },
    ],

    featuresTitle: 'اللي تحصل عليه كشريك',
    featuresSubtitle:
      'المنصة كاملة تحت علامتك التجارية، ومسعّرة لعمل تملكه أنت.',
    features: [
      {
        title: 'لوحة تحكم بعلامتك التجارية',
        description: 'شعارك واسم تطبيقك وإيميل الدعم، في المنتج كامل.',
      },
      {
        title: 'واجهة تجهيز وحدة',
        description: 'أنشئ حساباً جاهزاً بطلب واحد. بدون إعداد يدوي.',
      },
      {
        title: 'مفاتيح API لكل حساب',
        description: 'درّب الـ AI، اسحب المواقع، وأدر جهات الاتصال برمجياً.',
      },
      {
        title: 'تسعير حسب الاستخدام',
        description: 'ادفع مقابل اللي يستخدمه عملاؤك، وحدّد سعرك فوقه.',
      },
      {
        title: 'معزول وآمن',
        description: 'كل حساب معزول بالكامل، وبأمان بمستوى المؤسسات.',
      },
      {
        title: 'تركيب بدقائق',
        description:
          'ركّب رمز أداة الدردشة ويبدأ الـ AI بالعمل على موقع عميلك.',
      },
      {
        title: 'كل القنوات مشمولة',
        description: 'دردشة وإيميل وWhatsApp وسوشيال، لكل حساب تنشئه.',
      },
      {
        title: '+١٠٠ لغة',
        description: 'اخدم عملاء عملائك في أي مكان بالعالم.',
      },
      {
        title: 'فريق شركاء مخصّص',
        description: 'أشخاص بأسمائهم يساعدونك على الإطلاق والتكامل والنمو.',
      },
      {
        title: 'أنت تملك العلاقة',
        description: 'الفوترة والعلامة والحساب كلها تبقى معك.',
      },
    ],

    ctaHeading: 'جاهز تطلق منتج دعم بالـ AI باسمك أنت؟',
    ctaCopy:
      'أضف خط منتج جديد بدون ما تبني ذكاءً اصطناعياً، ولا بنية تحتية، ولا فريقاً. ومن الفكرة إلى الإطلاق، نمشي معك خلال أسابيع.',
    ctaPrimary: 'صير شريك معنا',
    ctaSecondary: 'اقرأ توثيق الشركاء',
  },
} as const;

export default function WhitelabelPage() {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';
  const c = isAr ? CONTENT.ar : CONTENT.en;
  const bentoSection = isAr ? BENTO_SECTION.ar : BENTO_SECTION.en;
  const bentoCards = isAr ? BENTO_CARDS_AR : BENTO_CARDS_EN;

  return (
    <div className="bg-[#fafaf9]">
      <HeroCentered
        title={
          <>
            {c.heroTitleA}
            <br />
            {c.heroTitleB}
          </>
        }
        subtitle={c.heroSubtitle}
        ctaText={c.heroCta}
      />

      {/* Booking.com / FareHarbor spotlight */}
      <section className="py-16 md:py-24">
        <div className="container">
          <SubtleEntrance>
            <div className="overflow-hidden rounded-3xl border border-neutral-300/70 bg-white p-8 md:p-14">
              <p className="mb-8 text-xs font-medium tracking-widest text-neutral-500 uppercase">
                {c.spotlightEyebrow}
              </p>
              <div className="grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-center">
                <div>
                  <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
                    {c.spotlightHeading}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
                    {c.spotlightCopy}
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                    <Image
                      src="/logos/logo-booking.svg"
                      alt="Booking.com"
                      width={150}
                      height={32}
                      className="h-6 w-auto object-contain opacity-80 md:h-7"
                    />
                    <Image
                      src="/logos/logo-fareharbor.svg"
                      alt="FareHarbor"
                      width={150}
                      height={32}
                      className="h-6 w-auto object-contain opacity-80 md:h-7"
                    />
                  </div>
                </div>
                <div className="flex flex-col items-start rounded-2xl bg-[#f3f1ec] p-8 md:items-center md:text-center">
                  <span className="font-serif text-5xl leading-none text-neutral-900 md:text-6xl">
                    {c.spotlightStat}
                  </span>
                  <span className="mt-3 max-w-[16rem] text-sm text-neutral-500">
                    {c.spotlightStatLabel}
                  </span>
                </div>
              </div>
            </div>
          </SubtleEntrance>
        </div>
      </section>

      <BentoGrid
        title={bentoSection.title}
        subtitle={bentoSection.subtitle}
        cards={bentoCards}
      />

      {/* Verticals grid */}
      <section className="py-16 md:py-32">
        <div className="container">
          <SubtleEntrance className="mb-14 max-w-2xl md:mb-20">
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.verticalsTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.verticalsSubtitle}
            </p>
          </SubtleEntrance>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {c.verticals.map((v, i) => {
              const Icon = VERTICAL_ICONS[i] ?? Building2;
              return (
                <div
                  key={v.title}
                  className="group relative overflow-hidden rounded-3xl border border-neutral-300/80 bg-white p-8 shadow-[0_1px_0_rgba(0,0,0,0.02)] transition-shadow duration-300 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.12)]"
                >
                  {/* Warm sand gradient backdrop — matches the bento cards. */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-white to-[#f3f1ec] via-[55%]"
                  />
                  {/* Subtle dot grid masked toward the bottom. */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-[0.05]"
                    style={{
                      backgroundImage:
                        'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
                      backgroundSize: '18px 18px',
                      maskImage:
                        'linear-gradient(to bottom, transparent 40%, black 90%)',
                      WebkitMaskImage:
                        'linear-gradient(to bottom, transparent 40%, black 90%)',
                    }}
                  />
                  <div className="relative">
                    <Icon
                      className="h-6 w-6 text-neutral-900"
                      strokeWidth={1.5}
                    />
                    <h3 className="mt-6 font-serif text-lg !font-normal text-neutral-900">
                      {v.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-500">
                      {v.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Three steps */}
      <section className="py-16 md:py-24">
        <div className="container">
          <SubtleEntrance className="mb-14 max-w-2xl md:mb-16">
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.stepsTitle}
            </h2>
          </SubtleEntrance>
          <div className="grid gap-px overflow-hidden rounded-3xl border border-neutral-300/80 bg-neutral-300/60 md:grid-cols-3">
            {c.steps.map((s, i) => (
              <div key={s.step} className="bg-[#fafaf9] p-8 md:p-10">
                <span className="font-serif text-3xl text-neutral-300 tabular-nums">
                  {isAr
                    ? `٠${['١', '٢', '٣', '٤', '٥'][i] ?? i + 1}`
                    : `0${i + 1}`}
                </span>
                <h3 className="mt-5 font-serif text-lg !font-normal text-neutral-900 md:text-xl">
                  {s.step}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500 md:text-base">
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features list */}
      <section className="py-16 md:py-32">
        <div className="container">
          <SubtleEntrance className="mb-14 max-w-2xl md:mb-20">
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.featuresTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.featuresSubtitle}
            </p>
          </SubtleEntrance>
          <ul className="grid sm:grid-cols-2 sm:gap-x-12">
            {c.features.map((f) => (
              <li
                key={f.title}
                className="flex gap-4 border-b border-neutral-200 py-5"
              >
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <h3 className="font-sans text-base text-neutral-900 md:text-lg">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600 md:text-base">
                    {f.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CustomerVideos hideEyebrow />

      {/* Partner CTA band */}
      <section className="py-16 md:py-24">
        <div className="container">
          <SubtleEntrance>
            <div className="rounded-3xl bg-neutral-950 px-8 py-14 text-center md:px-14 md:py-20">
              <h2 className="mx-auto max-w-2xl font-serif text-2xl text-balance text-white md:text-4xl">
                {c.ctaHeading}
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-neutral-400 md:text-base">
                {c.ctaCopy}
              </p>
              <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button
                  asChild
                  className="group h-12 rounded-full bg-white px-7 text-base font-medium text-neutral-950 hover:bg-neutral-100"
                >
                  <Link
                    href="/demo"
                    prefetch
                    aria-label={
                      t.common['Learn more about Open and book a demo']
                    }
                  >
                    {c.ctaPrimary}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 rounded-full border-white/20 bg-transparent px-7 text-base font-medium text-white hover:bg-white/10 hover:text-white"
                >
                  <a href={DOCS_URL} target="_blank" rel="noopener noreferrer">
                    {c.ctaSecondary}
                  </a>
                </Button>
              </div>
            </div>
          </SubtleEntrance>
        </div>
      </section>

      <Testimonials />
    </div>
  );
}
